import React, { FC, useEffect, useRef } from 'react'
import { Formik, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

import { Button, FormLabel, Input } from '@infologistics/frontend-libraries'

import { useAppDispatch, useAppSelector } from '@hooks'
import { Modal, SignerFullnameLength } from '@const/consts'
import { resetModal } from '@store/modules/modal'
import { getIsNextTask, getTaskType } from '@utils/envelope'
import { getSubmitButtonTranslationKey } from '../Footer/utils'
import { IFullNameFormData, IFullNameModalProps as IProps } from './types'
import { fullNameValidationSchema } from './validationSchema'
import { setFullNameMarks } from './utils'

import styles from './FullNameModal.module.scss'

const FullNameModal: FC<IProps> = ({
  onConfirm,
  setFieldValue,
  values: markFormValues,
}) => {
  const { t } = useTranslation(['envelope', 'common'])
  const dispatch = useAppDispatch()
  const { modal } = useAppSelector(state => state.modal)
  const { taskCounter } = useAppSelector(state => state.shared)
  const formikRef = useRef<FormikProps<IFullNameFormData>>(null)
  const isNextTask = getIsNextTask()
  const signerField = isNextTask ? 'nextTaskSignerFullName' : 'signerFullName'
  const marksField = isNextTask ? 'nextTaskMarks' : 'marks'
  const currentMarks = markFormValues[marksField] ?? {}
  const initialFullName = markFormValues[signerField] ?? ''
  const taskType = getTaskType()
  const submitButtonText = taskType
    ? t(getSubmitButtonTranslationKey(taskType, taskCounter))
    : t('envelope:actions.finish')

  useEffect(() => {
    if (modal === Modal.FULL_NAME) {
      formikRef.current?.resetForm({values: {fullName: initialFullName}})
    }
  }, [modal, initialFullName])

  const handleSubmit = ({ fullName: value }: IFullNameFormData): void => {
    const fullName = value.trim()
    const marks = setFullNameMarks(currentMarks, fullName)
    const values = {
      ...markFormValues,
      [signerField]: fullName,
      [marksField]: marks,
    }

    setFieldValue(signerField, fullName, false)
    setFieldValue(marksField, marks, false)
    dispatch(resetModal())
    onConfirm(values)
  }

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={{fullName: initialFullName}}
      validationSchema={fullNameValidationSchema}
      onSubmit={handleSubmit}
    >
      {(props) => {
        const fullNameError = props.touched.fullName && props.errors.fullName

        return (
          <form noValidate onSubmit={props.handleSubmit}>
            <header className='p-4 modal-header'>
              <h4 className='mb-0'>{t('envelope:signer.fullname')}</h4>
            </header>
            <div className='p-4 font-xs modal-body'>
              <FormLabel htmlFor='fullName'>
                {t('envelope:signer.fullname')}
              </FormLabel>
              <Input
                id='fullName'
                name='fullName'
                value={props.values.fullName}
                onChange={props.handleChange}
                onBlur={props.handleBlur}
                required
                maxLength={SignerFullnameLength.MAX}
                hasError={!!fullNameError}
                errorText={fullNameError ? t(fullNameError) : undefined}
              />
            </div>
            <footer className='my-0 p-4 modal-footer'>
              <div className={styles.modal_btns_wrapper}>
                <Button theme='primary' type='submit'>
                  {submitButtonText}
                </Button>
                <Button onClick={() => dispatch(resetModal())}>
                  {t('common:cancel')}
                </Button>
              </div>
            </footer>
          </form>
        )
      }}
    </Formik>
  )
}

export default FullNameModal
